import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import {
  parentPortfolio as parentPortfolioSchema,
  portfolio as portfolioSchema,
} from '../lib/schema.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { selectEntities } from './entities.js';
import { selectCurrentOrganizationId } from './auth.js';

/**
 * PORTFOLIO MODULE
 *
 * This module manages both portfolios and parent portfolios in a single file because:
 * 1. The API returns them as a hierarchical structure (parent portfolios containing child portfolios)
 * 2. They share the same fetch/search operations (retrievePortfolios gets the entire hierarchy)
 * 3. Special entities span both domains (no_parent_portfolio contains regular portfolios)
 * 4. Many selectors traverse parent→child relationships and would create circular dependencies if split
 *
 * The data model reflects the business domain where portfolios are not independent entities
 * but are children of parent portfolios.
 */

// API Actions

const RETRIEVE_PORTFOLIO_REQUEST =
  'changesync/portfolio/RETRIEVE_PORTFOLIO_REQUEST';
const RETRIEVE_PORTFOLIO_SUCCESS =
  'changesync/portfolio/RETRIEVE_PORTFOLIO_SUCCESS';
const RETRIEVE_PORTFOLIO_FAILURE =
  'changesync/portfolio/RETRIEVE_PORTFOLIO_FAILURE';

const RETRIEVE_PARENT_PORTFOLIO_REQUEST =
  'changesync/portfolio/RETRIEVE_PARENT_PORTFOLIO_REQUEST';
const RETRIEVE_PARENT_PORTFOLIO_SUCCESS =
  'changesync/portfolio/RETRIEVE_PARENT_PORTFOLIO_SUCCESS';
const RETRIEVE_PARENT_PORTFOLIO_FAILURE =
  'changesync/portfolio/RETRIEVE_PARENT_PORTFOLIO_FAILURE';

const RETRIEVE_PORTFOLIOS_REQUEST =
  'changesync/portfolio/RETRIEVE_PORTFOLIOS_REQUEST';
const RETRIEVE_PORTFOLIOS_SUCCESS =
  'changesync/portfolio/RETRIEVE_PORTFOLIOS_SUCCESS';
const RETRIEVE_PORTFOLIOS_FAILURE =
  'changesync/portfolio/RETRIEVE_PORTFOLIOS_FAILURE';

const CREATE_UPDATE_PORTFOLIO_REQUEST =
  'changesync/portfolio/CREATE_UPDATE_PORTFOLIO_REQUEST';
const CREATE_UPDATE_PORTFOLIO_SUCCESS =
  'changesync/portfolio/CREATE_UPDATE_PORTFOLIO_SUCCESS';
const CREATE_UPDATE_PORTFOLIO_FAILURE =
  'changesync/portfolio/CREATE_UPDATE_PORTFOLIO_FAILURE';

const UPDATE_CHANGE_PORTFOLIO_FIELDS_REQUEST =
  'changesync/portfolio/UPDATE_CHANGE_PORTFOLIO_FIELDS_REQUEST';
const UPDATE_CHANGE_PORTFOLIO_FIELDS_SUCCESS =
  'changesync/portfolio/UPDATE_CHANGE_PORTFOLIO_FIELDS_SUCCESS';
const UPDATE_CHANGE_PORTFOLIO_FIELDS_FAILURE =
  'changesync/portfolio/UPDATE_CHANGE_PORTFOLIO_FIELDS_FAILURE';

const FINISH_UPDATE_PORTFOLIO_REQUEST =
  'changesync/portfolio/FINISH_UPDATE_PORTFOLIO_REQUEST';
const FINISH_UPDATE_PORTFOLIO_SUCCESS =
  'changesync/portfolio/FINISH_UPDATE_PORTFOLIO_SUCCESS';
const FINISH_UPDATE_PORTFOLIO_FAILURE =
  'changesync/portfolio/FINISH_UPDATE_PORTFOLIO_FAILURE';
const RESET_DRAFT_ID = 'changesync/portfolio/RESET_DRAFT_ID';

// Search API Actions
const SEARCH_PORTFOLIOS_REQUEST =
  'changesync/portfolio/SEARCH_PORTFOLIOS_REQUEST';
const SEARCH_PORTFOLIOS_SUCCESS =
  'changesync/portfolio/SEARCH_PORTFOLIOS_SUCCESS';
const SEARCH_PORTFOLIOS_FAILURE =
  'changesync/portfolio/SEARCH_PORTFOLIOS_FAILURE';

// Search Actions
const SEARCH_PORTFOLIOS = 'changesync/portfolio/SEARCH_PORTFOLIOS';

const DELETE_PORTFOLIO_REQUEST =
  'changesync/portfolio/DELETE_PORTFOLIO_REQUEST';
const DELETE_PORTFOLIO_SUCCESS =
  'changesync/portfolio/DELETE_PORTFOLIO_SUCCESS';
const DELETE_PORTFOLIO_FAILURE =
  'changesync/portfolio/DELETE_PORTFOLIO_FAILURE';

const CREATE_PARENT_PORTFOLIO_REQUEST =
  'changesync/portfolio/CREATE_PARENT_PORTFOLIO_REQUEST';
const CREATE_PARENT_PORTFOLIO_SUCCESS =
  'changesync/portfolio/CREATE_PARENT_PORTFOLIO_SUCCESS';
const CREATE_PARENT_PORTFOLIO_FAILURE =
  'changesync/portfolio/CREATE_PARENT_PORTFOLIO_FAILURE';

const UPDATE_PARENT_PORTFOLIO_REQUEST =
  'changesync/portfolio/UPDATE_PARENT_PORTFOLIO_REQUEST';
const UPDATE_PARENT_PORTFOLIO_SUCCESS =
  'changesync/portfolio/UPDATE_PARENT_PORTFOLIO_SUCCESS';
const UPDATE_PARENT_PORTFOLIO_FAILURE =
  'changesync/portfolio/UPDATE_PARENT_PORTFOLIO_FAILURE';

const DELETE_PARENT_PORTFOLIO_REQUEST =
  'changesync/portfolio/DELETE_PARENT_PORTFOLIO_REQUEST';
const DELETE_PARENT_PORTFOLIO_SUCCESS =
  'changesync/portfolio/DELETE_PARENT_PORTFOLIO_SUCCESS';
const DELETE_PARENT_PORTFOLIO_FAILURE =
  'changesync/portfolio/DELETE_PARENT_PORTFOLIO_FAILURE';

// Initial State

const initialState = {
  portfolioDraftId: null,

  retrievePortfolios: {
    parentPortfolioIds: [],
    query: '',
    pending: false,
    success: false,
    error: null,
  },

  searchPortfolios: {
    parentPortfolioIds: [],
    query: '',
    pending: false,
    success: false,
    error: null,
  },

  retrievePortfolio: {
    pending: false,
    success: false,
    error: null,
  },
};

// Reducer

export default (state = initialState, action) => {
  switch (action.type) {
    case CREATE_UPDATE_PORTFOLIO_SUCCESS:
      return {
        ...state,
        portfolioDraftId: action.payload.result,
      };

    case FINISH_UPDATE_PORTFOLIO_SUCCESS:
      return {
        ...state,
        portfolioDraftId: null,
      };

    case RESET_DRAFT_ID:
      return {
        ...state,
        portfolioDraftId: null,
      };

    case RETRIEVE_PORTFOLIO_REQUEST:
      return {
        ...state,
        retrievePortfolio: {
          ...state.retrievePortfolio,
          pending: true,
          success: false,
          error: null,
        },
      };

    case RETRIEVE_PORTFOLIO_SUCCESS:
      return {
        ...state,
        retrievePortfolio: {
          ...state.retrievePortfolio,
          pending: false,
          success: true,
          error: null,
        },
      };
    case RETRIEVE_PORTFOLIO_FAILURE:
      return {
        ...state,
        retrievePortfolio: {
          ...state.retrievePortfolio,
          pending: false,
          success: false,
          error: action.error,
        },
      };

    case RETRIEVE_PORTFOLIOS_REQUEST:
      return {
        ...state,
        retrievePortfolios: {
          ...state.retrievePortfolios,
          pending: true,
          success: false,
          error: null,
        },
      };

    case RETRIEVE_PORTFOLIOS_SUCCESS:
      return {
        ...state,
        retrievePortfolios: {
          ...state.retrievePortfolios,
          parentPortfolioIds: action.payload.result,
          pending: false,
          success: true,
          error: null,
        },
      };
    case RETRIEVE_PORTFOLIOS_FAILURE:
      return {
        ...state,
        retrievePortfolios: {
          ...state.retrievePortfolios,
          pending: false,
          success: false,
          error: action.error,
        },
      };

    case SEARCH_PORTFOLIOS_REQUEST:
      return {
        ...state,
        searchPortfolios: {
          ...state.searchPortfolios,
          pending: true,
          success: false,
          error: null,
        },
      };
    case SEARCH_PORTFOLIOS_SUCCESS:
      return {
        ...state,
        searchPortfolios: {
          ...state.searchPortfolios,
          parentPortfolioIds: action.payload.result,
          query: action.meta.query,
          pending: false,
          success: true,
        },
      };
    case SEARCH_PORTFOLIOS_FAILURE:
      return {
        ...state,
        searchPortfolios: {
          ...state.searchPortfolios,
          pending: false,
          error: action.error,
        },
      };

    case DELETE_PORTFOLIO_SUCCESS:
      return {
        ...state,
        portfolioDraftId: null,
      };

    default:
      return state;
  }
};

// Action Creators

export const retrievePortfolio = (id) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_PORTFOLIO_REQUEST,
      RETRIEVE_PORTFOLIO_SUCCESS,
      RETRIEVE_PORTFOLIO_FAILURE,
    ],
    endpoint: `portfolios/${id}`,
    method: HTTP_METHODS.GET,
    schema: portfolioSchema,
    disabledCamelizeKeys: true,
  },
});

export const retrieveParentPortfolio = (id) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_PARENT_PORTFOLIO_REQUEST,
      RETRIEVE_PARENT_PORTFOLIO_SUCCESS,
      RETRIEVE_PARENT_PORTFOLIO_FAILURE,
    ],
    endpoint: `parent_portfolios/${id}`,
    method: HTTP_METHODS.GET,
    schema: parentPortfolioSchema,
  },
});

export const retrievePortfolios = () => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_PORTFOLIOS_REQUEST,
        RETRIEVE_PORTFOLIOS_SUCCESS,
        RETRIEVE_PORTFOLIOS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/portfolios`,
      method: HTTP_METHODS.GET,
      schema: [parentPortfolioSchema],
      disabledCamelizeKeys: true,
    },
  });
};

export const searchPortfolios = (query) => (dispatch, getState) => {
  const organizationId = selectCurrentOrganizationId(getState());

  return dispatch({
    [CALL_API]: {
      types: [
        SEARCH_PORTFOLIOS_REQUEST,
        SEARCH_PORTFOLIOS_SUCCESS,
        SEARCH_PORTFOLIOS_FAILURE,
      ],
      endpoint: `organizations/${organizationId}/portfolios`,
      method: HTTP_METHODS.GET,
      schema: [parentPortfolioSchema],
      query,
    },
    meta: {
      query,
      debounce: {
        time: 500,
        key: SEARCH_PORTFOLIOS,
      },
    },
  });
};

export const createPortfolioDraft =
  (portfolioId, portfolio) => (dispatch, getState) => {
    const organizationId = selectCurrentOrganizationId(getState());
    const url = `organizations/${organizationId}/portfolios`;
    const method = HTTP_METHODS.POST;
    return dispatch({
      [CALL_API]: {
        types: [
          CREATE_UPDATE_PORTFOLIO_REQUEST,
          CREATE_UPDATE_PORTFOLIO_SUCCESS,
          CREATE_UPDATE_PORTFOLIO_FAILURE,
        ],
        endpoint: url,
        method,
        schema: portfolioSchema,
        body: { portfolio },
        disabledDecamelizeKeys: true, // we need to prevent any key conversion and send them "as is"
        disabledCamelizeKeys: true,
        // query: { finish },
      },
    });
  };

export const updatePortfolioDraft = (id, portfolio, finish, deleteDraft) => {
  return {
    [CALL_API]: {
      types: [
        CREATE_UPDATE_PORTFOLIO_REQUEST,
        CREATE_UPDATE_PORTFOLIO_SUCCESS,
        CREATE_UPDATE_PORTFOLIO_FAILURE,
      ],
      endpoint: `portfolios/${id}`,
      method: HTTP_METHODS.PUT,
      schema: portfolioSchema,
      body: { portfolio },
      disabledDecamelizeKeys: true,
      disabledCamelizeKeys: true,
      query: {
        finish,
        delete_draft: !!deleteDraft,
      },
    },
  };
};

export const finishUpdatePortfolioDraft = (id, portfolio, deleteDraft) => {
  return {
    [CALL_API]: {
      types: [
        FINISH_UPDATE_PORTFOLIO_REQUEST,
        FINISH_UPDATE_PORTFOLIO_SUCCESS,
        FINISH_UPDATE_PORTFOLIO_FAILURE,
      ],
      endpoint: `portfolios/${id}`,
      method: HTTP_METHODS.PUT,
      schema: portfolioSchema,
      body: { portfolio },
      disabledDecamelizeKeys: true,
      disabledCamelizeKeys: true,
      query: {
        finish: true,
        delete_draft: deleteDraft,
      },
    },
  };
};

export const updatePortfolioCustomFields = (id, customFields) => ({
  [CALL_API]: {
    types: [
      UPDATE_CHANGE_PORTFOLIO_FIELDS_REQUEST,
      UPDATE_CHANGE_PORTFOLIO_FIELDS_SUCCESS,
      UPDATE_CHANGE_PORTFOLIO_FIELDS_FAILURE,
    ],
    endpoint: `portfolios/${id}/update_custom_fields`,
    method: HTTP_METHODS.PUT,
    body: customFields,
  },
});

export const deletePortfolio = (id) => ({
  [CALL_API]: {
    types: [
      DELETE_PORTFOLIO_REQUEST,
      DELETE_PORTFOLIO_SUCCESS,
      DELETE_PORTFOLIO_FAILURE,
    ],
    endpoint: `portfolios/${id}`,
    method: HTTP_METHODS.DELETE,
  },
});

export const createParentPortfolio =
  (parentPortfolio) => (dispatch, getState) => {
    const organizationId = selectCurrentOrganizationId(getState());

    return dispatch({
      [CALL_API]: {
        types: [
          CREATE_PARENT_PORTFOLIO_REQUEST,
          CREATE_PARENT_PORTFOLIO_SUCCESS,
          CREATE_PARENT_PORTFOLIO_FAILURE,
        ],
        endpoint: `organizations/${organizationId}/parent_portfolios`,
        method: HTTP_METHODS.POST,
        schema: parentPortfolioSchema,
        body: { parentPortfolio },
      },
    });
  };

export const updateParentPortfolio = (id, parentPortfolio) => ({
  [CALL_API]: {
    types: [
      UPDATE_PARENT_PORTFOLIO_REQUEST,
      UPDATE_PARENT_PORTFOLIO_SUCCESS,
      UPDATE_PARENT_PORTFOLIO_FAILURE,
    ],
    endpoint: `parent_portfolios/${id}`,
    method: HTTP_METHODS.PUT,
    schema: parentPortfolioSchema,
    body: { parentPortfolio },
  },
});

export const deleteParentPortfolio = (id) => ({
  [CALL_API]: {
    types: [
      DELETE_PARENT_PORTFOLIO_REQUEST,
      DELETE_PARENT_PORTFOLIO_SUCCESS,
      DELETE_PARENT_PORTFOLIO_FAILURE,
    ],
    endpoint: `parent_portfolios/${id}`,
    method: HTTP_METHODS.DELETE,
  },
});

// Selectors
export const selectRetrievePortfoliosState = (state) =>
  state.portfolio.retrievePortfolios;
export const selectRetrievePortfolioState = (state) =>
  state.portfolio.retrievePortfolio;
export const selectSearchPortfolioState = (state) =>
  state.portfolio.searchPortfolios;

export const selectPortfolioDraftId = (state) =>
  state.portfolio.portfolioDraftId;
export const selectPortfolioDraft = (id) => (state) =>
  denormalize(id, portfolioSchema, state.entities);
export const selectPortfolio = (id) => (state) =>
  denormalize(id, portfolioSchema, state.entities);

// Parent Portfolio Selectors
const selectParentPortfolioIds = createSelector(
  selectRetrievePortfoliosState,
  (state) => state.parentPortfolioIds
);

// Get all parent portfolios (including special entities)
export const selectParentPortfoliosWithSpecial = createSelector(
  selectParentPortfolioIds,
  selectEntities,
  (parentPortfolioIds, entities) =>
    denormalize(parentPortfolioIds, [parentPortfolioSchema], entities)
);

// Get only real parent portfolios (excluding no_parent_portfolio)
export const selectParentPortfolios = createSelector(
  selectParentPortfoliosWithSpecial,
  (parentPortfolios) =>
    parentPortfolios.filter((pp) => pp.id !== 'no_parent_portfolio')
);

// Get the "No Parent Portfolio" section
export const selectNoParentPortfolioSection = createSelector(
  selectParentPortfoliosWithSpecial,
  (parentPortfolios) =>
    parentPortfolios.find((pp) => pp.id === 'no_parent_portfolio')
);

// Get portfolios without parent (excluding the no_portfolio entity)
export const selectUnassignedPortfolios = createSelector(
  selectNoParentPortfolioSection,
  (noParentSection) => {
    if (!noParentSection || !noParentSection.portfolios) return [];
    return noParentSection.portfolios.filter(
      (p) => p.id !== 'no_portfolio' && !p.draft
    );
  }
);

// Get the "No Portfolio" entity (projects with no portfolio at all)
export const selectNoPortfolioEntity = createSelector(
  selectNoParentPortfolioSection,
  (noParentSection) => {
    if (!noParentSection || !noParentSection.portfolios) return null;
    return noParentSection.portfolios.find((p) => p.id === 'no_portfolio');
  }
);

// Flat list of all portfolios (backward compatible)
export const selectPortfolios = createSelector(
  selectParentPortfoliosWithSpecial,
  (parentPortfolios) => {
    return parentPortfolios
      .flatMap((pp) => pp.portfolios || [])
      .filter((p) => p.id !== 'no_portfolio' && !p.draft);
  }
);

// Search results
const selectSearchPortfolioIds = createSelector(
  selectSearchPortfolioState,
  (state) => state.parentPortfolioIds
);

export const selectSearchParentPortfoliosResult = createSelector(
  selectSearchPortfolioIds,
  selectEntities,
  (parentPortfolioIds, entities) =>
    denormalize(parentPortfolioIds, [parentPortfolioSchema], entities)
);

// Backward compatible - returns flat array of portfolios for old containers
export const selectSearchPortfoliosResult = createSelector(
  selectSearchParentPortfoliosResult,
  (parentPortfolios) => {
    return parentPortfolios.flatMap(
      (parentPortfolio) => parentPortfolio.portfolios || []
    );
  }
);

// Get portfolios from search excluding special entities
export const selectSearchPortfoliosFiltered = createSelector(
  selectSearchPortfoliosResult,
  (portfolios) => portfolios.filter((p) => !p.draft && p.id !== 'no_portfolio')
);

// Get no_portfolio entity from search results
export const selectSearchNoPortfolioEntity = createSelector(
  selectSearchParentPortfoliosResult,
  (parentPortfolios) => {
    const noParentPortfolio = parentPortfolios.find(
      (pp) => pp.id === 'no_parent_portfolio'
    );
    if (!noParentPortfolio || !noParentPortfolio.portfolios) return null;
    return noParentPortfolio.portfolios.find((p) => p.id === 'no_portfolio');
  }
);

// Get individual parent portfolio by ID
export const selectParentPortfolio = (id) => (state) =>
  denormalize(id, parentPortfolioSchema, state.entities);
